// Iter 275k — /gold hub redesign inspired by tipmaster.ai's
// section-driven rhythm.  Adopts their density and card patterns
// (top-performers grid, hot-streaks strip, upcoming-matches carousel,
// AI-feature block, trust/transparency stats) while keeping the
// existing amber/gold luxury identity.  Every sub-route from the
// original hub remains reachable via CTA links.
//
// Data-live: every section pulls from the same endpoints already used
// by the sub-pages, so we don't ship a placeholder-only landing.

import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  Target, CalendarClock, FileSearch, Activity, ClipboardList,
  FlaskConical, ChevronRight, Flame, Trophy, Sparkles,
  ShieldCheck, LineChart, ArrowUpRight, Layers, Zap,
} from "lucide-react";
import GoldSectionLayout from "../components/GoldSectionLayout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { appTodayIso } from "../lib/appTime";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Hooks ───────────────────────────────────────────────────────────

const useJson = (path, deps = []) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancel = false;
    const token = typeof window !== "undefined" ? localStorage.getItem("ndaku_token") : null;
    fetch(`${API}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => !cancel && setData(d))
      .catch((e) => !cancel && setErr(String(e)));
    return () => { cancel = true; };
     
  }, deps);
  return { data, err };
};

// ── Small building blocks ───────────────────────────────────────────

const SectionHeader = ({ eyebrow, title, sub, icon: Icon }) => (
  <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
    <div>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-amber-500/70 font-black mb-2">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {eyebrow}
      </div>
      <h2 className="text-2xl md:text-3xl font-black tracking-tight text-amber-50" style={{ fontFamily: '"Playfair Display", "Fraunces", Georgia, serif' }}>
        {title}
      </h2>
      {sub && <p className="text-sm text-amber-100/60 mt-1.5 max-w-2xl">{sub}</p>}
    </div>
  </div>
);

const StatChip = ({ label, value, tone = "amber" }) => {
  const toneMap = {
    amber: "text-amber-200 border-amber-500/30 bg-amber-500/[0.06]",
    emerald: "text-emerald-200 border-emerald-500/30 bg-emerald-500/[0.06]",
    rose: "text-rose-200 border-rose-500/30 bg-rose-500/[0.06]",
    slate: "text-slate-200 border-slate-500/30 bg-slate-500/[0.06]",
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${toneMap[tone]}`}>
      <div className="text-[9px] uppercase tracking-[0.18em] opacity-70 font-bold">{label}</div>
      <div className="text-lg font-black font-mono tabular-nums mt-0.5">{value}</div>
    </div>
  );
};

const WChips = ({ streak = 0, size = "sm" }) => {
  const cls = size === "sm" ? "w-4 h-4 text-[9px]" : "w-6 h-6 text-[11px]";
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: Math.min(8, streak) }).map((_, i) => (
        <span
          key={i}
          className={`${cls} inline-flex items-center justify-center rounded-full bg-emerald-500/25 border border-emerald-400/50 text-emerald-100 font-black`}
        >
          W
        </span>
      ))}
      {streak > 8 && (
        <span className="ml-1 text-[10px] font-mono font-bold text-emerald-200">
          +{streak - 8}
        </span>
      )}
    </div>
  );
};

// ── Section: Hero ───────────────────────────────────────────────────

const HeroBlock = ({ liveHitRate, totalPicks }) => (
  <section
    className="relative overflow-hidden rounded-2xl border border-amber-500/25 bg-black p-8 md:p-14 mb-14"
    data-testid="gold-hero"
  >
    <div
      aria-hidden
      className="absolute inset-0 opacity-30 pointer-events-none"
      style={{
        background:
          "radial-gradient(60% 80% at 15% 0%, rgba(251,191,36,0.18), transparent 60%), " +
          "radial-gradient(50% 70% at 100% 100%, rgba(251,191,36,0.12), transparent 55%)",
      }}
    />
    <div
      aria-hidden
      className="absolute inset-0 opacity-[0.045] pointer-events-none"
      style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='4' height='4'><rect width='1' height='1' fill='%23fbbf24'/></svg>\")" }}
    />

    <div className="relative max-w-3xl">
      <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.24em] text-amber-300 mb-6">
        <Sparkles className="w-3 h-3" /> Gold · Institutional Intelligence
      </div>
      <h1
        className="text-5xl md:text-6xl xl:text-7xl font-black leading-[1.02] tracking-tight text-amber-50 mb-5"
        style={{ fontFamily: '"Playfair Display", "Fraunces", Georgia, serif' }}
      >
        Bet like the <span className="text-amber-400 italic">house</span>.
      </h1>
      <p className="text-lg text-amber-100/70 leading-relaxed max-w-2xl mb-8">
        Twenty prediction layers. One ensemble. Live edge on every fixture — with the full audit trail so you never bet blind again.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-10">
        <Button
          asChild
          size="lg"
          className="bg-gradient-to-r from-amber-500 to-yellow-400 text-black hover:from-amber-400 hover:to-yellow-300 font-black uppercase tracking-widest shadow-lg shadow-amber-500/25"
          data-testid="gold-hero-cta-picks"
        >
          <Link to="/gold/daily-picks">Today&apos;s Picks <ArrowUpRight className="ml-1.5 w-4 h-4" /></Link>
        </Button>
        <Button
          asChild
          size="lg"
          variant="outline"
          className="border-amber-500/50 text-amber-200 hover:bg-amber-500/10 hover:text-amber-100 font-bold uppercase tracking-widest"
          data-testid="gold-hero-cta-dossier"
        >
          <Link to="/gold/dossier">Open Match Dossier</Link>
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl">
        <StatChip
          label="Live hit rate"
          value={liveHitRate != null ? `${liveHitRate}%` : "—"}
          tone="emerald"
        />
        <StatChip
          label="Graded picks"
          value={totalPicks != null ? totalPicks.toLocaleString() : "—"}
        />
        <StatChip label="Prediction layers" value="20" />
        <StatChip label="Ensemble models" value="7" />
      </div>
    </div>
  </section>
);

// ── Section: Best Performing Slates ─────────────────────────────────

const SLATE_CARDS = [
  { to: "/gold/daily-picks",  title: "30 Picks of the Day",  icon: Target,        blurb: "Daily curated slate — every layer applied.", tag: "Flagship" },
  { to: "/gold/daily-picks",  title: "Halftime BTTS Acca",   icon: Flame,         blurb: "HT-scoring pickups auto-graded overnight.",  tag: "High-volume" },
  { to: "/gold/daily-picks",  title: "Double Chance Slate",  icon: ShieldCheck,   blurb: "Safety-first pool with edge over vig.",       tag: "Low-risk" },
  { to: "/gold/schedule",     title: "7-Day Schedule",       icon: CalendarClock, blurb: "Top-3 markets per fixture up to a week out.", tag: "Look-ahead" },
];

const SlateGrid = ({ hits, misses, hitRate }) => (
  <section className="mb-16" data-testid="gold-slates">
    <SectionHeader
      icon={Trophy}
      eyebrow="Best Performing Slates"
      title="The lineups working right now"
      sub="Live hit-rate straight from the accuracy tracker &mdash; no cherry-picked marketing numbers, no reset windows."
    />
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      {SLATE_CARDS.map((c) => {
        const Icon = c.icon;
        return (
          <Link
            key={c.title}
            to={c.to}
            data-testid={`gold-slate-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
            className="group relative rounded-xl border border-amber-500/25 bg-gradient-to-br from-amber-950/40 via-black to-black p-5 hover:border-amber-400/70 hover:shadow-2xl hover:shadow-amber-500/10 hover:-translate-y-0.5 transition-[transform,border-color,box-shadow] duration-300"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/25 group-hover:bg-amber-500/20 transition-colors">
                <Icon className="w-5 h-5 text-amber-300" />
              </div>
              <Badge className="bg-amber-500/10 text-amber-300 border-amber-500/25 text-[9px] uppercase tracking-widest font-bold">
                {c.tag}
              </Badge>
            </div>
            <h3 className="text-base font-black text-amber-50 mb-2 tracking-tight">{c.title}</h3>
            <p className="text-xs text-amber-100/60 leading-relaxed mb-4">{c.blurb}</p>
            <div className="flex items-center justify-between border-t border-amber-500/15 pt-3">
              <div>
                <div className="text-[9px] uppercase tracking-widest text-amber-100/50 mb-0.5">Live rate</div>
                <div className="text-lg font-black text-emerald-300 font-mono">
                  {hitRate != null ? `${hitRate}%` : "—"}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-amber-500/40 group-hover:text-amber-300 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        );
      })}
    </div>
    <div className="mt-4 text-center text-xs text-amber-100/50">
      {hits != null && misses != null
        ? `Backed by ${hits.toLocaleString()} verified hits vs ${misses.toLocaleString()} misses across the full archive.`
        : "Numbers stream live from the accuracy tracker."}
    </div>
  </section>
);

// ── Section: Hot Streaks (per-market live rates) ─────────────────────

const StreakStrip = ({ by_market }) => {
  const rows = useMemo(() => {
    if (!by_market) return [];
    // Server returns a list of {market_type, hits, misses, total, hit_rate_pct}.
    const list = Array.isArray(by_market)
      ? by_market
      : Object.entries(by_market).map(([market_type, s]) => ({ market_type, ...s }));
    return list
      .map((s) => ({
        market: s.market_type,
        hit_rate: s.hit_rate_pct,
        hits: s.hits,
        misses: s.misses,
        total: s.graded ?? s.total,
      }))
      .filter((r) => r.hit_rate != null && r.total >= 20)
      .sort((a, b) => b.hit_rate - a.hit_rate)
      .slice(0, 6);
  }, [by_market]);

  return (
    <section className="mb-16" data-testid="gold-streaks">
      <SectionHeader
        icon={Flame}
        eyebrow="Hot Streaks"
        title="Markets currently on a heater"
        sub="Rolling 30-day hit-rate across every graded prediction. Ordered by conviction, not by hype."
      />
      {rows.length === 0 ? (
        <div className="rounded-xl border border-amber-500/15 bg-black/50 p-8 text-center text-sm text-amber-100/50">
          Warming up the ensemble &mdash; first heat check available once today&apos;s picks settle.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((r) => {
            const streak = Math.min(8, Math.round((r.hit_rate / 100) * 8));
            const isHot = r.hit_rate >= 75;
            return (
              <div
                key={r.market}
                data-testid={`gold-streak-${r.market.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-xl border p-4 transition-all ${
                  isHot
                    ? "border-emerald-400/40 bg-gradient-to-br from-emerald-950/50 via-black to-black shadow-lg shadow-emerald-500/5"
                    : "border-amber-500/20 bg-black/60"
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-sm font-black text-amber-50 tracking-tight">{r.market}</div>
                    <div className="text-[10px] uppercase tracking-widest text-amber-100/50 mt-0.5">
                      {r.total.toLocaleString()} graded · 30d
                    </div>
                  </div>
                  <div className={`text-3xl font-black font-mono tabular-nums ${isHot ? "text-emerald-300" : "text-amber-300"}`}>
                    {r.hit_rate}%
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <WChips streak={streak} />
                  <span className="text-[10px] font-mono text-amber-100/40">
                    {r.hits}W / {r.misses}L
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-5 flex justify-center">
        <Button
          asChild
          variant="outline"
          className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10 hover:text-amber-100 uppercase tracking-widest font-bold text-xs"
          data-testid="gold-streaks-cta"
        >
          <Link to="/gold/accuracy">Open full accuracy tracker <ArrowUpRight className="ml-1.5 w-3.5 h-3.5" /></Link>
        </Button>
      </div>
    </section>
  );
};

// ── Section: AI-Powered Match Dossier ───────────────────────────────

const DOSSIER_FEATURES = [
  { icon: Layers,      title: "20 institutional layers", body: "ELO, Poisson, Dixon-Coles, Monte-Carlo, referee bias, xG regression, pitfall detector — each layer's contribution surfaced separately, not black-boxed." },
  { icon: Zap,         title: "Live edge vs the market", body: "Bookmaker odds cross-referenced in real time — spot the vig-beating price the second it opens." },
  { icon: FileSearch,  title: "Self-learning post-mortems", body: "Every miss is fed back into the model weights via the Miss Analyzer — the engine gets sharper every week." },
  { icon: ClipboardList, title: "Full audit trail",       body: "Every prediction ships with its rationale, the layers that agreed, and the layers that dissented." },
];

const DossierFeature = () => (
  <section className="mb-16" data-testid="gold-dossier-feature">
    <SectionHeader
      icon={FlaskConical}
      eyebrow="AI-Powered Match Dossier"
      title="Twenty layers. Zero guesswork."
      sub="What makes an institutional pick different from a hunch — every dossier ships with the receipts."
    />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {DOSSIER_FEATURES.map((f) => {
        const Icon = f.icon;
        return (
          <div
            key={f.title}
            className="group rounded-xl border border-amber-500/20 bg-gradient-to-br from-black to-amber-950/20 p-6 hover:border-amber-400/50 transition-colors"
          >
            <div className="flex items-start gap-4">
              <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/25 shrink-0">
                <Icon className="w-5 h-5 text-amber-300" />
              </div>
              <div>
                <h3 className="text-base font-black text-amber-50 mb-1.5 tracking-tight">{f.title}</h3>
                <p className="text-sm text-amber-100/60 leading-relaxed">{f.body}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
    <div className="mt-5 flex justify-center gap-3 flex-wrap">
      <Button
        asChild
        className="bg-gradient-to-r from-amber-500 to-yellow-400 text-black hover:from-amber-400 hover:to-yellow-300 font-black uppercase tracking-widest"
        data-testid="gold-dossier-cta"
      >
        <Link to="/gold/dossier">Open a dossier <ArrowUpRight className="ml-1.5 w-4 h-4" /></Link>
      </Button>
      <Button
        asChild
        variant="outline"
        className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10 hover:text-amber-100 uppercase tracking-widest font-bold"
        data-testid="gold-dossier-expert-cta"
      >
        <Link to="/gold/expert">Expert Mode</Link>
      </Button>
    </div>
  </section>
);

// ── Section: 7-Day Ensemble Schedule preview ────────────────────────

const SchedulePreview = ({ fixtures }) => {
  const top = (fixtures || []).slice(0, 6);
  return (
    <section className="mb-16" data-testid="gold-schedule-preview">
      <SectionHeader
        icon={CalendarClock}
        eyebrow="Hot Matches Today"
        title="Where the ensemble is loudest"
        sub="Highest-conviction fixtures on today's board — click through for every layer of the read."
      />
      {top.length === 0 ? (
        <div className="rounded-xl border border-amber-500/15 bg-black/50 p-8 text-center text-sm text-amber-100/50">
          Today&apos;s board is being compiled. Peek back in a few minutes.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {top.map((f, i) => (
            <Link
              key={f.match_id || i}
              to={f.match_id ? `/match/${f.match_id}` : "/gold/schedule"}
              className="group rounded-xl border border-amber-500/20 bg-black/70 p-5 hover:border-amber-400/60 hover:bg-black/90 transition-colors"
              data-testid={`gold-schedule-card-${i}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-widest text-amber-500/70 font-black">
                  {f.league_name}
                </div>
                <div className="text-[10px] font-mono text-amber-100/50">
                  {f.kickoff_local || (f.kickoff_iso || "").slice(11, 16)}
                </div>
              </div>
              <div className="text-base font-black text-amber-50 tracking-tight mb-4">
                {f.home_team?.name} <span className="text-amber-500/50 mx-1">vs</span> {f.away_team?.name}
              </div>
              <div className="flex items-center justify-between border-t border-amber-500/15 pt-3">
                <div className="text-xs text-amber-100/70">
                  <span className="text-amber-100/50 mr-1">Top market:</span>
                  {f.markets?.[0]?.market_type || "—"}
                </div>
                <div className="text-lg font-black text-amber-300 font-mono">
                  {f.markets?.[0]?.confidence ? `${Math.round(f.markets[0].confidence)}%` : "—"}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
      <div className="mt-5 flex justify-center">
        <Button
          asChild
          variant="outline"
          className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10 hover:text-amber-100 uppercase tracking-widest font-bold text-xs"
          data-testid="gold-schedule-cta"
        >
          <Link to="/gold/schedule">See the full 7-day slate <ArrowUpRight className="ml-1.5 w-3.5 h-3.5" /></Link>
        </Button>
      </div>
    </section>
  );
};

// ── Section: Trust / Transparency ───────────────────────────────────

const TrustBlock = ({ track }) => {
  const tot = track?.total_settled || 0;
  const wins = track?.hits || 0;
  const losses = track?.misses || 0;
  const rate = tot ? Math.round((wins / tot) * 100) : null;

  const pillars = [
    { icon: LineChart,   title: "Every pick locked",     body: "Once a prediction ships, it's snapshotted. No silent edits, no revisionist scoreboards." },
    { icon: ShieldCheck, title: "Verified hit-rate",     body: "The percentage on every card comes from the same archive that grades our own bets." },
    { icon: FlaskConical,title: "Self-learning models",  body: "Misses feed straight into the Miss Analyzer, retuning the ensemble weights every night." },
    { icon: Sparkles,    title: "Institutional stack",   body: "Bivariate Poisson, ELO, Dixon-Coles and 17 more layers — the same stack pro trading desks use." },
  ];

  return (
    <section className="mb-16" data-testid="gold-trust">
      <SectionHeader
        icon={ShieldCheck}
        eyebrow="Trust & Transparency"
        title="The receipts, not the marketing"
        sub={rate != null
          ? `${wins.toLocaleString()} wins vs ${losses.toLocaleString()} losses over the last 30 days — every one graded on the same ledger.`
          : "Every graded pick lives in the accuracy tracker — no cherry-picking, no reset windows."}
      />
      <div className="rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-950/30 via-black to-black p-6 md:p-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatChip label="30-day win rate" value={rate != null ? `${rate}%` : "—"} tone="emerald" />
          <StatChip label="Total picks graded" value={tot.toLocaleString()} />
          <StatChip label="Verified wins" value={wins.toLocaleString()} tone="emerald" />
          <StatChip label="Recorded losses" value={losses.toLocaleString()} tone="rose" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pillars.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="flex items-start gap-3 p-4 rounded-lg border border-amber-500/15 bg-black/40">
                <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/25 shrink-0">
                  <Icon className="w-4 h-4 text-amber-300" />
                </div>
                <div>
                  <div className="text-sm font-black text-amber-50 mb-1">{p.title}</div>
                  <div className="text-xs text-amber-100/60 leading-relaxed">{p.body}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Button
            asChild
            variant="outline"
            className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10 hover:text-amber-100 uppercase tracking-widest font-bold text-xs"
            data-testid="gold-trust-accuracy-cta"
          >
            <Link to="/gold/accuracy">Full accuracy ledger</Link>
          </Button>
          <Button
            asChild
            variant="outline"
            className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10 hover:text-amber-100 uppercase tracking-widest font-bold text-xs"
            data-testid="gold-trust-miss-cta"
          >
            <Link to="/gold/miss-analyzer">Miss analyzer</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

// ── Section: Closer CTA ─────────────────────────────────────────────

const CloserCTA = () => (
  <section
    className="rounded-2xl border border-amber-500/40 bg-gradient-to-r from-amber-500 to-yellow-400 p-8 md:p-14 text-center relative overflow-hidden"
    data-testid="gold-closer"
  >
    <div aria-hidden className="absolute inset-0 opacity-10" style={{
      background:
        "radial-gradient(40% 60% at 30% 40%, rgba(0,0,0,0.35), transparent 60%), " +
        "radial-gradient(40% 60% at 70% 60%, rgba(0,0,0,0.35), transparent 60%)",
    }} />
    <h2
      className="relative text-3xl md:text-5xl font-black tracking-tight text-black mb-4"
      style={{ fontFamily: '"Playfair Display", "Fraunces", Georgia, serif' }}
    >
      Ready to bet with the receipts?
    </h2>
    <p className="relative text-black/70 max-w-2xl mx-auto mb-6 text-base md:text-lg">
      Pull up today&apos;s slate, open any dossier, and see the twenty-layer read the moment you land.
    </p>
    <div className="relative inline-flex gap-3 flex-wrap justify-center">
      <Button
        asChild
        size="lg"
        className="bg-black text-amber-300 hover:bg-black/90 hover:text-amber-200 font-black uppercase tracking-widest"
        data-testid="gold-closer-cta"
      >
        <Link to="/gold/daily-picks">Today&apos;s picks <ArrowUpRight className="ml-1.5 w-4 h-4" /></Link>
      </Button>
    </div>
  </section>
);

// ── The page ────────────────────────────────────────────────────────

export default function GoldOverviewPage() {
  const today = appTodayIso();
  const { data: accuracy } = useJson("/api/gold/accuracy/overview", []);
  const { data: track } = useJson("/api/accuracy/track_record?days=30", []);
  const { data: schedule } = useJson(`/api/schedule/day?date=${today}`, [today]);

  // Flatten schedule for "hot matches today".
  const hotFixtures = useMemo(() => {
    if (!schedule?.leagues) return [];
    const out = [];
    for (const lg of schedule.leagues) {
      for (const f of lg.fixtures || []) {
        if (!f.has_prediction) continue;
        const best = (f.markets || [])
          .slice()
          .sort((a, b) => (b.confidence || 0) - (a.confidence || 0))[0];
        out.push({
          match_id: f.match_id,
          league_name: lg.league_name,
          home_team: f.home_team,
          away_team: f.away_team,
          kickoff_iso: f.kickoff_iso,
          markets: best ? [best] : [],
        });
      }
    }
    return out
      .sort((a, b) => (b.markets[0]?.confidence || 0) - (a.markets[0]?.confidence || 0));
  }, [schedule]);

  return (
    <GoldSectionLayout
      title="Intelligence Portal"
      subtitle="Twenty-layer prediction engine · self-learning · every layer wired to real match data."
      testid="gold-overview-page"
    >
      <HeroBlock
        liveHitRate={accuracy?.global?.hit_rate_pct ?? null}
        totalPicks={accuracy?.global?.graded ?? accuracy?.global?.total ?? null}
      />
      <SlateGrid
        hits={accuracy?.global?.hits}
        misses={accuracy?.global?.misses}
        hitRate={accuracy?.global?.hit_rate_pct}
      />
      <StreakStrip by_market={accuracy?.by_market} />
      <DossierFeature />
      <SchedulePreview fixtures={hotFixtures} />
      <TrustBlock track={track} />
      <CloserCTA />
    </GoldSectionLayout>
  );
}

